/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import * as ModalActionCreators from '@app/actions/ModalActionCreators';
import {modal} from '@app/actions/ModalActionCreators';
import type {CheckoutPaymentMethod, PriceIds} from '@app/actions/PremiumActionCreators';
import * as PremiumActionCreators from '@app/actions/PremiumActionCreators';
import * as ToastActionCreators from '@app/actions/ToastActionCreators';
import {ConfirmModal} from '@app/components/modals/ConfirmModal';
import {ApiResponseError} from '@app/lib/ApiResponseError';
import {Logger} from '@app/lib/Logger';
import {openExternalUrl} from '@app/utils/NativeUtils';
import type {PricingMode} from '@app/utils/PricingUtils';
import {APIErrorCodes} from '@fluxer/constants/src/ApiErrorCodes';
import {useLingui} from '@lingui/react/macro';
import {useCallback, useState} from 'react';

const logger = new Logger('useCheckoutActions');

type Plan = 'monthly' | 'yearly' | 'gift_1_month' | 'gift_1_year';
type CheckoutPromptKind = 'payment' | 'localized_card_preapproval';

// const LOCALIZED_CARD_PREAPPROVAL_CURRENCIES = new Set(['BRL', 'INR', 'PLN', 'TRY']);

// TEMP: localized card preapproval disabled — users can buy localized prices without card verification.
function requiresLocalizedCardPreapproval(_plan: Plan, _currency: string | null | undefined, _isGift: boolean): boolean {
	return false;
}

function alternativePaymentMethodForCurrency(
	currency: string | null | undefined,
	isGift: boolean,
	plan: Plan,
): CheckoutPaymentMethod | null {
	if (isGift || (plan !== 'monthly' && plan !== 'yearly')) {
		return null;
	}
	if (currency === 'BRL') return 'pix';
	if (currency === 'INR') return 'upi';
	return null;
}

export const useCheckoutActions = (
	priceIds: PriceIds | null,
	countryCode: string | null,
	pricingMode: PricingMode,
	isGiftSubscription: boolean,
	mobileEnabled: boolean,
) => {
	const {t} = useLingui();
	const [loadingCheckout, setLoadingCheckout] = useState(false);

	const getAlternativePaymentMethodPrompt = useCallback(
		(
			currency: string | null | undefined,
			method: CheckoutPaymentMethod,
		): {description: string; primaryText: string} => {
			if (method === 'pix' && currency === 'BRL') {
				return {
					description: t`Pay with Pix Automático to authorize recurring charges directly from your Brazilian bank. Or choose Use Card to enter a credit card on Stripe's next screen.`,
					primaryText: t`Use Pix`,
				};
			}
			if (method === 'upi' && currency === 'INR') {
				return {
					description: t`Pay with UPI to set up an RBI-compliant e-mandate from your Indian bank. Or choose Use Card to enter a credit card on Stripe's next screen.`,
					primaryText: t`Use UPI`,
				};
			}
			return {
				description: t`Choose how you'd like to pay for this subscription.`,
				primaryText: t`Use Alternative Method`,
			};
		},
		[t],
	);

	const getLocalizedCardPrompt = useCallback(
		(currency: string | null | undefined): {description: string; secondaryText: string} => {
			switch (currency) {
				case 'BRL':
					return {
						description: t`Use Local Card to verify a card issued in Brazil before the paid BRL checkout. Choose Other Methods if you'd rather try Pix or another payment method Stripe offers on the next screen.`,
						secondaryText: t`Other Methods`,
					};
				case 'INR':
					return {
						description: t`Use Local Card to verify a card issued in India before the paid INR checkout. Choose Other Methods if you'd rather try UPI or another payment method Stripe offers on the next screen.`,
						secondaryText: t`Other Methods`,
					};
				case 'PLN':
					return {
						description: t`Use Local Card to verify a card issued in Poland before the paid PLN checkout. BLIK does not support subscriptions in Stripe Checkout, so continue only if you want Stripe's standard payment screen instead.`,
						secondaryText: t`Continue to Checkout`,
					};
				case 'TRY':
					return {
						description: t`Use Local Card to verify a card issued in Türkiye before the paid TRY checkout. There isn't an app-based local subscription method here, so continuing will take you to Stripe's standard payment screen.`,
						secondaryText: t`Continue to Checkout`,
					};
				default:
					return {
						description: t`Use Local Card to verify a card issued in your billing country before the paid checkout. Choose Other Methods for any other payment method Stripe offers on the next screen.`,
						secondaryText: t`Other Methods`,
					};
			}
		},
		[t],
	);

	const handleCheckoutError = useCallback(
		(error: unknown) => {
			logger.error('Failed to create checkout session', error);

			if (error instanceof ApiResponseError) {
				const body = error.payload;
				if (body && typeof body === 'object' && 'code' in body && typeof body.code === 'string') {
					if (body.code === APIErrorCodes.EMAIL_VERIFICATION_REQUIRED) {
						ToastActionCreators.error(t`Verify your email to purchase Fluxer Plutonium.`);
						return;
					}

					if (body.code === APIErrorCodes.PREMIUM_PURCHASE_BLOCKED) {
						ToastActionCreators.error(
							t`You're already Lifetime. Starting a new subscription isn't allowed. You can still buy gifts for others.`,
						);
						return;
					}
				}
			}

			ToastActionCreators.error(t`Failed to start checkout. Please try again.`);
		},
		[t],
	);

	const handleSelectPlan = useCallback(
		async (plan: Plan) => {
			if (loadingCheckout) return;

			logger.info('Plan selected', {plan, isGiftSubscription});

			if (isGiftSubscription && (plan === 'monthly' || plan === 'yearly')) {
				ToastActionCreators.error(
					t`You're currently on a gift subscription. It won't renew. You can redeem more gift codes to extend it. Recurring subscriptions can be started after your gift time ends.`,
				);
				return;
			}

			if (!priceIds) {
				logger.error('Price IDs not loaded yet');
				ToastActionCreators.error(t`Please wait for pricing information to load.`);
				return;
			}

			const planConfig: Record<Plan, {id: string | null; gift?: boolean}> = {
				monthly: {id: priceIds.monthly ?? null},
				yearly: {id: priceIds.yearly ?? null},
				gift_1_month: {id: priceIds.gift_1_month ?? null, gift: true},
				gift_1_year: {id: priceIds.gift_1_year ?? null, gift: true},
			};

			const selected = planConfig[plan];
			const priceId = selected.id;
			const isGift = selected.gift ?? false;

			if (!priceId) {
				logger.error('Price ID not available for plan', {plan});
				ToastActionCreators.error(t`This plan is not available at the moment. Please contact support.`);
				return;
			}

			const openCheckoutUrl = async (
				checkoutUrl: string,
				{
					promptKind = 'payment',
					skipMobilePrompt = false,
				}: {promptKind?: CheckoutPromptKind; skipMobilePrompt?: boolean} = {},
			) => {
				if (mobileEnabled && !skipMobilePrompt) {
					ModalActionCreators.push(
						modal(() => (
							<ConfirmModal
								title={promptKind === 'localized_card_preapproval' ? t`Verify Card` : t`Complete Payment`}
								description={
									promptKind === 'localized_card_preapproval'
										? t`Stripe will first verify that your card is eligible for localized pricing, then continue you to payment. Return to Fluxer once you've completed it.`
										: t`You are now navigating to Stripe to complete the payment. Return to Fluxer once you've completed it!`
								}
								primaryText={t`OK`}
								primaryVariant="primary"
								secondaryText={t`Cancel`}
								onPrimary={() => {
									void openExternalUrl(checkoutUrl);
								}}
							/>
						)),
					);
					return;
				}

				await openExternalUrl(checkoutUrl);
			};

			const startCheckout = async ({
				skipMobilePrompt = false,
				paymentMethod,
			}: {skipMobilePrompt?: boolean; paymentMethod?: CheckoutPaymentMethod} = {}) => {
				setLoadingCheckout(true);
				try {
					const checkoutUrl = await PremiumActionCreators.createCheckoutSession(
						priceId,
						countryCode ?? undefined,
						isGift,
						pricingMode,
						paymentMethod,
					);
					await openCheckoutUrl(checkoutUrl, {promptKind: 'payment', skipMobilePrompt});
				} catch (error) {
					handleCheckoutError(error);
				} finally {
					setLoadingCheckout(false);
				}
			};

			const startLocalizedCardPreapproval = async ({skipMobilePrompt = false}: {skipMobilePrompt?: boolean} = {}) => {
				if (!countryCode) {
					ToastActionCreators.error(t`Localized card verification is not available right now. Please try again later.`);
					return;
				}

				setLoadingCheckout(true);
				try {
					const checkoutUrl = await PremiumActionCreators.createLocalizedCardPreapprovalSession(
						priceId,
						countryCode,
						pricingMode,
					);
					await openCheckoutUrl(checkoutUrl, {
						promptKind: 'localized_card_preapproval',
						skipMobilePrompt,
					});
				} catch (error) {
					handleCheckoutError(error);
				} finally {
					setLoadingCheckout(false);
				}
			};

			if (requiresLocalizedCardPreapproval(plan, priceIds.currency, isGift)) {
				const localizedCardPrompt = getLocalizedCardPrompt(priceIds.currency);
				ModalActionCreators.push(
					modal(() => (
						<ConfirmModal
							title={t`Choose Payment Method`}
							description={localizedCardPrompt.description}
							primaryText={t`Use Local Card`}
							primaryVariant="primary"
							secondaryText={localizedCardPrompt.secondaryText}
							onPrimary={() => startLocalizedCardPreapproval({skipMobilePrompt: true})}
							onSecondary={() => {
								void startCheckout({skipMobilePrompt: true});
							}}
						/>
					)),
				);
				return;
			}

			const altPaymentMethod = alternativePaymentMethodForCurrency(priceIds.currency, isGift, plan);
			if (altPaymentMethod) {
				const altPrompt = getAlternativePaymentMethodPrompt(priceIds.currency, altPaymentMethod);
				ModalActionCreators.push(
					modal(() => (
						<ConfirmModal
							title={t`Choose Payment Method`}
							description={altPrompt.description}
							primaryText={altPrompt.primaryText}
							primaryVariant="primary"
							secondaryText={t`Use Card`}
							onPrimary={() => {
								void startCheckout({skipMobilePrompt: true, paymentMethod: altPaymentMethod});
							}}
							onSecondary={() => {
								void startCheckout({skipMobilePrompt: true});
							}}
						/>
					)),
				);
				return;
			}

			await startCheckout();
		},
		[
			handleCheckoutError,
			loadingCheckout,
			priceIds,
			countryCode,
			getLocalizedCardPrompt,
			getAlternativePaymentMethodPrompt,
			isGiftSubscription,
			mobileEnabled,
			pricingMode,
			t,
		],
	);

	return {
		loadingCheckout,
		handleSelectPlan,
	};
};
